import React, { useState, useEffect, useRef, FunctionComponent } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import {
  FaInstagram, FaYoutube, FaTiktok, FaTwitter, FaFacebook, FaPodcast, FaBlog,
  FaDollarSign, FaChartLine, FaLink, FaGift, FaUsers, FaCheckCircle,
} from 'react-icons/fa';

const API_URL = process.env.REACT_APP_STRAPI_API_URL || 'https://www.eatmeatapp.com';

const Icon = (C: any) => C as FunctionComponent<{ className?: string }>;
const FaInstagramI = Icon(FaInstagram);
const FaYoutubeI = Icon(FaYoutube);
const FaTiktokI = Icon(FaTiktok);
const FaTwitterI = Icon(FaTwitter);
const FaFacebookI = Icon(FaFacebook);
const FaPodcastI = Icon(FaPodcast);
const FaBlogI = Icon(FaBlog);
const FaDollarSignI = Icon(FaDollarSign);
const FaChartLineI = Icon(FaChartLine);
const FaLinkI = Icon(FaLink);
const FaGiftI = Icon(FaGift);
const FaUsersI = Icon(FaUsers);
const FaCheckCircleI = Icon(FaCheckCircle);

const platforms = [
  { key: 'instagram', label: 'Instagram', Icon: FaInstagramI, color: 'from-pink-500 to-purple-500' },
  { key: 'youtube', label: 'YouTube', Icon: FaYoutubeI, color: 'from-red-500 to-red-600' },
  { key: 'tiktok', label: 'TikTok', Icon: FaTiktokI, color: 'from-gray-800 to-black' },
  { key: 'twitter', label: 'Twitter / X', Icon: FaTwitterI, color: 'from-blue-400 to-blue-500' },
  { key: 'facebook', label: 'Facebook', Icon: FaFacebookI, color: 'from-blue-600 to-blue-700' },
  { key: 'podcast', label: 'Podcast', Icon: FaPodcastI, color: 'from-purple-500 to-purple-700' },
  { key: 'blog', label: 'Blog', Icon: FaBlogI, color: 'from-green-500 to-green-700' },
  { key: 'other', label: 'Other', Icon: FaLinkI, color: 'from-gray-500 to-gray-700' },
];

const benefits = [
  { Icon: FaDollarSignI, title: 'Recurring Commission', desc: 'Earn a share of every subscription your audience makes — paid monthly.', color: 'bg-green-50 text-green-600' },
  { Icon: FaChartLineI, title: 'Real-time Dashboard', desc: 'Track signups, subscriptions, and earnings in your private portal.', color: 'bg-blue-50 text-blue-600' },
  { Icon: FaLinkI, title: 'Custom Referral Code', desc: 'Get a unique code + App Store and Google Play campaign links.', color: 'bg-purple-50 text-purple-600' },
  { Icon: FaGiftI, title: 'Free Premium Access', desc: 'Full access to every feature of EatMeat so you can speak from experience.', color: 'bg-pink-50 text-pink-600' },
  { Icon: FaUsersI, title: 'Growing Community', desc: 'Tap into a passionate, fast-growing carnivore and ketovore audience.', color: 'bg-orange-50 text-orange-600' },
  { Icon: FaCheckCircleI, title: 'No Quotas, No Pressure', desc: 'Share when you want. No monthly minimums, no strict rules.', color: 'bg-teal-50 text-teal-600' },
];

const steps = [
  { n: '1', title: 'Apply', desc: 'Fill out the short form with your socials and audience size.', icon: '📝' },
  { n: '2', title: 'Get Approved', desc: 'We review and send your unique code, campaign links, and portal credentials.', icon: '✅' },
  { n: '3', title: 'Share & Earn', desc: 'Post about EatMeat, share your code, and watch your earnings grow.', icon: '💰' },
];

// Scroll-triggered reveal
const useInView = (threshold = 0.15) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); io.disconnect(); } },
      { threshold, rootMargin: '0px 0px -50px 0px' }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return { ref, inView };
};

// Reveal wrapper
const Reveal: React.FC<{ children: React.ReactNode; delay?: number; className?: string; direction?: 'up' | 'left' | 'right' }> = ({ children, delay = 0, className = '', direction = 'up' }) => {
  const { ref, inView } = useInView();
  const t = direction === 'up' ? 'translateY(24px)' : direction === 'left' ? 'translateX(-24px)' : 'translateX(24px)';
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'translate(0,0)' : t,
      transition: `opacity 0.7s ease-out ${delay}s, transform 0.7s ease-out ${delay}s`,
    }}>{children}</div>
  );
};

interface Props {
  isLoggedIn?: boolean;
}

const InfluencerLanding: React.FC<Props> = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [platform, setPlatform] = useState('instagram');
  const [socialUrl, setSocialUrl] = useState('');
  const [followers, setFollowers] = useState('');
  const [message, setMessage] = useState('');
  const [country, setCountry] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('paypal');
  const [paymentEmail, setPaymentEmail] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankAccountHolder, setBankAccountHolder] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [bankRoutingSwift, setBankRoutingSwift] = useState('');
  const [taxId, setTaxId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [earningsDisplay, setEarningsDisplay] = useState(0);

  // Animated earnings counter that slowly climbs to demo potential
  useEffect(() => {
    const target = 2847;
    const duration = 2500;
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const p = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setEarningsDisplay(Math.floor(ease * target));
      if (p < 1) requestAnimationFrame(tick);
    };
    const timer = setTimeout(() => requestAnimationFrame(tick), 500);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name || !email || !platform) {
      setError('Please fill in name, email, and platform.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/referrals/apply`, {
        name, email, platform, social_url: socialUrl, followers, message,
        country,
        payment_method: paymentMethod,
        payment_email: paymentEmail,
        bank_name: bankName,
        bank_account_holder: bankAccountHolder,
        bank_account_number: bankAccountNumber,
        bank_routing_swift: bankRoutingSwift,
        tax_id: taxId,
      });
      setSubmitted(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen overflow-hidden">
      <Helmet>
        <title>Influencer Program — EatMeat</title>
        <meta name="description" content="Join the EatMeat Influencer Program. Earn recurring commission, get a custom referral code, and grow with the fastest-growing carnivore and ketovore meal planning app." />
      </Helmet>

      <style>{`
        @keyframes floatUp { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-14px) rotate(3deg); } }
        @keyframes floatUpAlt { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-18px) rotate(-4deg); } }
        @keyframes pulse-slow { 0%,100% { opacity: 0.3; } 50% { opacity: 0.6; } }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes shine { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }
        @keyframes barGrow { 0% { transform: scaleY(0); } 100% { transform: scaleY(1); } }
        @keyframes drawPath { 0% { stroke-dashoffset: 500; } 100% { stroke-dashoffset: 0; } }
        @keyframes tickerUp { 0%,100% { transform: translateY(100%); opacity: 0; } 4%,16% { transform: translateY(0); opacity: 1; } 20% { transform: translateY(-100%); opacity: 0; } 21%,100% { transform: translateY(100%); opacity: 0; } }
        @keyframes coinSpin { 0%,100% { transform: rotateY(0deg); } 50% { transform: rotateY(180deg); } }
        .animate-float { animation: floatUp 5s ease-in-out infinite; }
        .animate-float-alt { animation: floatUpAlt 6.5s ease-in-out infinite; }
        .shimmer-text { background: linear-gradient(90deg, #10b981 0%, #f59e0b 30%, #10b981 60%, #f59e0b 100%); background-size: 200% 100%; -webkit-background-clip: text; background-clip: text; color: transparent; animation: shimmer 6s linear infinite; }
        .shine-btn { position: relative; overflow: hidden; }
        .shine-btn::after { content: ''; position: absolute; top: 0; left: 0; width: 50%; height: 100%; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent); animation: shine 3s infinite; }
        .tilt-card { transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1); }
        .tilt-card:hover { transform: perspective(1000px) rotateX(4deg) rotateY(-4deg) translateY(-4px); }
        .coin-flip { animation: coinSpin 6s ease-in-out infinite; transform-style: preserve-3d; }
      `}</style>

      {/* ═══ Page header strip ═══ */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 md:py-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 rounded-full px-2.5 py-0.5 text-[11px] font-semibold mb-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Now accepting applications
              </div>
              <h1 className="text-lg md:text-xl font-extrabold tracking-tight leading-tight text-gray-900">Influencer Program</h1>
              <p className="text-gray-500 text-xs">Earn recurring commission · Free premium · 24/7 portal</p>
            </div>
            <Link to="/influencer" className="hidden sm:inline-flex items-center gap-2 bg-primary text-white rounded-full px-4 py-1.5 text-sm font-semibold hover:bg-primary/90 transition-colors shadow-md shadow-primary/20">
              Partner Login →
            </Link>
          </div>
        </div>
      </div>

      {/* ═══ Hero with animated dashboard mock ═══ */}
      <section className="relative overflow-hidden">
        {/* Ambient blobs */}
        <div className="pointer-events-none absolute -top-20 -left-20 w-96 h-96 bg-green-200 rounded-full blur-3xl opacity-30" style={{ animation: 'pulse-slow 8s ease-in-out infinite' }} />
        <div className="pointer-events-none absolute top-60 -right-20 w-[28rem] h-[28rem] bg-orange-200 rounded-full blur-3xl opacity-25" style={{ animation: 'pulse-slow 10s ease-in-out infinite 1s' }} />
        <div className="pointer-events-none absolute bottom-0 left-1/3 w-64 h-64 bg-purple-200 rounded-full blur-3xl opacity-20" style={{ animation: 'pulse-slow 12s ease-in-out infinite 2s' }} />

        <div className="relative max-w-6xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-10 md:gap-12 items-center">
            {/* LEFT — copy */}
            <Reveal direction="left">
              <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1 text-xs font-semibold text-gray-700 mb-5 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Partner program · 10% commission
              </div>
              <h2 className="text-4xl md:text-6xl font-extrabold text-gray-900 tracking-tight leading-[1.05] mb-5">
                Earn <span className="shimmer-text">monthly</span> with EatMeat
              </h2>
              <p className="text-lg text-gray-500 mb-8 max-w-md">Share the #1 carnivore & ketovore meal planning app with your audience and earn recurring commission — paid monthly, for as long as they subscribe.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="#apply" className="group px-8 py-3.5 bg-primary text-white rounded-xl font-semibold shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 shine-btn">
                  Apply Now
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </a>
                <Link to="/influencer" className="px-8 py-3.5 border border-gray-200 text-gray-700 bg-white rounded-xl font-semibold hover:border-primary hover:text-primary transition-colors text-center">
                  Partner Login
                </Link>
              </div>

              {/* Mini stats row */}
              <div className="mt-8 flex items-center gap-4 text-sm">
                <div className="flex -space-x-2">
                  {['🎬', '📷', '🎙️', '✍️'].map((e, i) => (
                    <div key={i} className="w-9 h-9 rounded-full bg-white border-2 border-gray-50 flex items-center justify-center shadow-sm text-sm" style={{ animation: `floatUp ${4 + i * 0.5}s ease-in-out infinite` }}>{e}</div>
                  ))}
                </div>
                <div>
                  <div className="font-semibold text-gray-800">200+ creators</div>
                  <div className="text-xs text-gray-500">earning with EatMeat</div>
                </div>
              </div>
            </Reveal>

            {/* RIGHT — earnings dashboard mock */}
            <Reveal direction="right" delay={0.2}>
              <div className="relative">
                {/* Floating badges */}
                <div className="absolute -top-6 -left-4 z-10 bg-white rounded-2xl border border-gray-100 shadow-xl px-4 py-3 flex items-center gap-2 animate-float-alt">
                  <div className="w-9 h-9 rounded-xl bg-green-50 flex items-center justify-center text-green-600 text-lg">💰</div>
                  <div>
                    <div className="text-[10px] text-gray-400 font-semibold uppercase">This month</div>
                    <div className="text-sm font-bold text-gray-800">$2,847</div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 z-10 bg-white rounded-2xl border border-gray-100 shadow-xl px-4 py-3 flex items-center gap-2 animate-float">
                  <span className="text-2xl">📈</span>
                  <div>
                    <div className="text-[10px] text-gray-400 font-semibold uppercase">Growth</div>
                    <div className="text-sm font-bold text-green-600">+42%</div>
                  </div>
                </div>
                <div className="absolute top-1/2 -right-6 z-10 bg-white rounded-2xl border border-gray-100 shadow-xl px-3 py-2 flex items-center gap-2 animate-float-alt">
                  <span className="text-xl">👥</span>
                  <div>
                    <div className="text-[10px] text-gray-400 font-semibold uppercase">Signups</div>
                    <div className="text-sm font-bold text-gray-800">428</div>
                  </div>
                </div>

                {/* Main dashboard mock */}
                <div className="bg-white rounded-3xl border border-gray-100 shadow-2xl shadow-gray-200/60 p-6 tilt-card">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <div className="text-xs text-gray-400 font-medium">INFLUENCER PORTAL</div>
                      <div className="text-lg font-bold text-gray-800">Your Earnings</div>
                    </div>
                    <div className="flex items-center gap-1.5 bg-green-50 text-green-700 rounded-full px-2 py-0.5 text-[10px] font-bold">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Live
                    </div>
                  </div>

                  {/* Big earnings counter */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-100 rounded-2xl p-5 mb-4">
                    <div className="text-[10px] font-bold text-green-700 uppercase tracking-wider mb-1">Earned this month</div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-extrabold text-gray-900 tabular-nums">${earningsDisplay.toLocaleString()}</span>
                      <span className="text-sm font-bold text-green-600">+42%</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">vs. last month</div>
                  </div>

                  {/* Mini chart */}
                  <div className="bg-gray-50 rounded-xl p-3 mb-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-semibold text-gray-600">📊 Monthly trend</span>
                      <span className="text-xs font-bold text-gray-400">6 months</span>
                    </div>
                    <svg viewBox="0 0 200 40" className="w-full" preserveAspectRatio="none" style={{ height: 40 }}>
                      <defs>
                        <linearGradient id="earnGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
                          <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path d="M 0 30 L 30 28 L 60 24 L 90 22 L 120 18 L 150 14 L 180 10 L 200 8 L 200 40 L 0 40 Z" fill="url(#earnGrad)" style={{ animation: 'fadeSlide 1.2s ease-out 0.6s backwards' }} />
                      <path d="M 0 30 L 30 28 L 60 24 L 90 22 L 120 18 L 150 14 L 180 10 L 200 8" stroke="#10b981" strokeWidth={2} fill="none" strokeLinecap="round" strokeDasharray={500} strokeDashoffset={500} style={{ animation: 'drawPath 2s ease-out 0.8s forwards' }} />
                    </svg>
                  </div>

                  {/* Recent signups bar */}
                  <div>
                    <div className="text-xs font-semibold text-gray-600 mb-2">Daily signups · last 7 days</div>
                    <div className="flex items-end justify-between gap-1 h-10">
                      {[4, 6, 5, 8, 7, 9, 8].map((h, i) => (
                        <div key={i} className="flex-1 bg-gradient-to-t from-primary to-orange-400 rounded-sm origin-bottom" style={{ height: `${h * 10}%`, animation: `barGrow 0.8s cubic-bezier(0.4,0,0.2,1) ${0.9 + i * 0.08}s backwards` }} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ Stats strip ═══ */}
      <section className="bg-white border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {[
              { n: '10%', l: 'Commission Rate', icon: '💰', tint: 'bg-green-50 text-green-600 border-green-100' },
              { n: 'Monthly', l: 'Recurring Payouts', icon: '📅', tint: 'bg-blue-50 text-blue-600 border-blue-100' },
              { n: 'Free', l: 'Premium Access', icon: '✨', tint: 'bg-purple-50 text-purple-600 border-purple-100' },
              { n: '24/7', l: 'Portal Access', icon: '🔓', tint: 'bg-orange-50 text-orange-600 border-orange-100' },
            ].map((s, i) => (
              <Reveal key={i} delay={0.05 * i}>
                <div className={`relative rounded-2xl border ${s.tint} p-4 md:p-5 overflow-hidden group cursor-default h-full`}>
                  <div className="absolute -top-4 -right-4 text-6xl opacity-20 group-hover:scale-125 group-hover:rotate-12 transition-transform duration-500">{s.icon}</div>
                  <div className="relative">
                    <div className="text-2xl md:text-3xl font-extrabold">{s.n}</div>
                    <div className="text-[11px] md:text-xs font-semibold mt-1 opacity-80">{s.l}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Benefits ═══ */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <Reveal>
            <div className="text-center mb-12">
              <div className="text-xs font-bold text-primary uppercase tracking-widest mb-2">Perks & Benefits</div>
              <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 tracking-tight mb-4">Why Join Our Program?</h2>
              <p className="text-gray-500 max-w-2xl mx-auto">Built for creators who care about health, nutrition, and quality content.</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b, i) => (
              <Reveal key={i} delay={0.08 * i}>
                <div className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-gray-100 h-full tilt-card relative overflow-hidden">
                  {/* Number badge */}
                  <div className="absolute top-4 right-4 w-7 h-7 rounded-full bg-gray-100 text-gray-400 text-xs font-bold flex items-center justify-center group-hover:bg-primary group-hover:text-white group-hover:scale-110 transition-all">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div className={`w-12 h-12 rounded-xl ${b.color} flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300`}>
                    <b.Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 pr-8">{b.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{b.desc}</p>
                  {/* Shine on hover */}
                  <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute inset-y-0 -left-full w-1/2 bg-gradient-to-r from-transparent via-primary/10 to-transparent group-hover:translate-x-[400%] transition-transform duration-1000" />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ How It Works ═══ */}
      <section className="py-16 md:py-24 bg-gray-900 text-white relative overflow-hidden">
        {/* Ambient blobs */}
        <div className="pointer-events-none absolute -top-20 left-1/4 w-80 h-80 bg-primary rounded-full blur-3xl opacity-20" style={{ animation: 'pulse-slow 8s ease-in-out infinite' }} />
        <div className="pointer-events-none absolute bottom-0 right-1/4 w-96 h-96 bg-green-500 rounded-full blur-3xl opacity-15" style={{ animation: 'pulse-slow 10s ease-in-out infinite 1s' }} />

        <div className="relative max-w-6xl mx-auto px-4 md:px-6">
          <Reveal>
            <div className="text-center mb-12">
              <div className="text-xs font-bold text-primary uppercase tracking-widest mb-2">3 Simple Steps</div>
              <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4">How It Works</h2>
              <p className="text-white/70">Start earning in minutes.</p>
            </div>
          </Reveal>

          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Connecting line — desktop only */}
            <div className="hidden md:block absolute top-8 left-[16.66%] right-[16.66%] h-px bg-gradient-to-r from-transparent via-primary to-transparent opacity-30" />
            {steps.map((s, i) => (
              <Reveal key={s.n} delay={0.15 * i}>
                <div className="relative text-center group">
                  <div className="relative inline-block mb-4">
                    <div className="absolute inset-0 bg-primary blur-xl opacity-50 group-hover:opacity-75 transition-opacity" />
                    <div className="relative w-16 h-16 bg-gradient-to-br from-primary to-orange-500 text-white rounded-2xl mx-auto flex items-center justify-center text-2xl font-extrabold shadow-2xl shadow-primary/50 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                      {s.n}
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center text-lg shadow-lg" style={{ animation: `floatUp ${3 + i * 0.5}s ease-in-out infinite` }}>{s.icon}</div>
                  </div>
                  <h3 className="text-xl font-bold mb-2">{s.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed max-w-xs mx-auto">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Live signup ticker */}
          <Reveal delay={0.5}>
            <div className="max-w-md mx-auto mt-12 bg-white/5 backdrop-blur border border-white/10 rounded-2xl px-4 py-3 flex items-center gap-3">
              <div className="relative w-2.5 h-2.5 flex-shrink-0">
                <div className="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-75" />
                <div className="relative w-2.5 h-2.5 bg-green-400 rounded-full" />
              </div>
              <div className="flex-1 h-5 relative overflow-hidden">
                {[
                  { n: 'Emma from Sydney', a: 'just got approved' },
                  { n: 'Marcus from Berlin', a: 'earned $1,420 this month' },
                  { n: 'Jade from LA', a: 'referred 47 signups' },
                  { n: 'Ravi from Mumbai', a: 'hit 10k earnings' },
                  { n: 'Sophie from Paris', a: 'joined the program' },
                ].map((t, i) => (
                  <div key={i} className="absolute inset-0 text-xs md:text-sm text-white/80 truncate" style={{ animation: `tickerUp 20s linear ${i * 4}s infinite`, opacity: 0 }}>
                    <strong className="text-white">{t.n}</strong>
                    <span className="text-white/50"> {t.a}</span>
                  </div>
                ))}
              </div>
              <span className="text-[9px] md:text-[10px] font-bold text-green-400 uppercase tracking-wider flex-shrink-0">Live</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ Platforms ═══ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <Reveal>
            <div className="text-center mb-10">
              <div className="text-xs font-bold text-primary uppercase tracking-widest mb-2">Universal</div>
              <h2 className="text-2xl md:text-4xl font-extrabold text-gray-900 mb-3">Works on every platform</h2>
              <p className="text-gray-500">Share EatMeat wherever your audience is.</p>
            </div>
          </Reveal>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {platforms.slice(0, 7).map((p, i) => (
              <Reveal key={p.key} delay={0.04 * i}>
                <div className={`bg-gradient-to-br ${p.color} text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 font-semibold hover:-translate-y-1 hover:shadow-xl transition-all duration-300 cursor-default group`}>
                  <p.Icon className="w-5 h-5 group-hover:scale-125 group-hover:rotate-12 transition-transform" /> {p.label}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Apply Form ═══ */}
      <section id="apply" className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16 md:py-24">
        {/* Ambient blobs */}
        <div className="pointer-events-none absolute -top-20 left-1/3 w-96 h-96 bg-primary rounded-full blur-3xl opacity-20" style={{ animation: 'pulse-slow 9s ease-in-out infinite' }} />
        <div className="pointer-events-none absolute bottom-0 right-1/4 w-80 h-80 bg-green-500 rounded-full blur-3xl opacity-15" style={{ animation: 'pulse-slow 11s ease-in-out infinite 1s' }} />
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

        <div className="relative max-w-2xl mx-auto px-4 md:px-6">
          <Reveal>
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-primary/20 text-primary rounded-full px-3 py-1 text-xs font-bold mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" /> Now accepting
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4">Apply to Join</h2>
              <p className="text-white/70">We review applications within 2–3 business days.</p>
            </div>
          </Reveal>

          {submitted ? (
            <Reveal>
              <div className="bg-green-500/10 border border-green-400/30 rounded-3xl p-10 text-center shadow-2xl">
                <div className="relative inline-block mb-4">
                  <div className="absolute inset-0 bg-green-400 blur-2xl opacity-30" />
                  <div className="relative w-20 h-20 rounded-full bg-green-500 flex items-center justify-center mx-auto shadow-lg" style={{ animation: 'floatUp 3s ease-in-out infinite' }}>
                    <FaCheckCircleI className="w-12 h-12 text-white" />
                  </div>
                </div>
                <h3 className="text-3xl font-extrabold mb-3">Application Received! 🎉</h3>
                <p className="text-white/80 mb-6 max-w-md mx-auto">We'll review your submission and reach out to <span className="text-green-400 font-bold">{email}</span> within a few business days.</p>
                <Link to="/" className="inline-block bg-gradient-to-r from-primary to-orange-500 px-8 py-3 rounded-full font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all">Back to Home</Link>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur rounded-3xl p-6 md:p-8 space-y-4 border border-white/10 shadow-2xl">
                {error && (
                  <div className="bg-red-500/20 border border-red-400/30 text-red-100 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <span>⚠️</span> {error}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-white/70 uppercase tracking-wider mb-1.5">Full Name *</label>
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} required
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-4 focus:ring-primary/20 focus:border-primary focus:bg-white/15 transition-all"
                      placeholder="Jane Smith" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-white/70 uppercase tracking-wider mb-1.5">Email *</label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-4 focus:ring-primary/20 focus:border-primary focus:bg-white/15 transition-all"
                      placeholder="you@example.com" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-white/70 uppercase tracking-wider mb-1.5">Primary Platform *</label>
                  <div className="grid grid-cols-4 gap-2">
                    {platforms.slice(0, 8).map((p) => (
                      <button key={p.key} type="button" onClick={() => setPlatform(p.key)}
                        className={`flex flex-col items-center gap-1 py-3 rounded-xl text-xs font-bold transition-all ${platform === p.key ? `bg-gradient-to-br ${p.color} text-white shadow-lg scale-105` : 'bg-white/10 text-white/70 hover:bg-white/15'}`}>
                        <p.Icon className="w-4 h-4" />
                        <span className="text-[10px]">{p.label.split(' ')[0]}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-white/70 uppercase tracking-wider mb-1.5">Profile / Channel URL</label>
                  <input type="url" value={socialUrl} onChange={(e) => setSocialUrl(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-4 focus:ring-primary/20 focus:border-primary focus:bg-white/15 transition-all"
                    placeholder="https://instagram.com/yourhandle" />
                </div>

                <div>
                  <label className="block text-xs font-bold text-white/70 uppercase tracking-wider mb-1.5">Follower Count</label>
                  <input type="text" value={followers} onChange={(e) => setFollowers(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-4 focus:ring-primary/20 focus:border-primary focus:bg-white/15 transition-all"
                    placeholder="e.g. 25,000" />
                </div>

                <div>
                  <label className="block text-xs font-bold text-white/70 uppercase tracking-wider mb-1.5">Tell us about yourself</label>
                  <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-4 focus:ring-primary/20 focus:border-primary focus:bg-white/15 transition-all resize-none"
                    placeholder="What's your niche? How do you plan to share EatMeat?" />
                </div>

                {/* ═══ Payment section ═══ */}
                <div className="pt-2 mt-2 border-t border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">💰</span>
                    <h3 className="text-sm font-extrabold text-white uppercase tracking-wider">Payment details</h3>
                  </div>
                  <p className="text-xs text-white/60 mb-4">How should we pay your commission? Your details are encrypted and only visible to our admins.</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-bold text-white/70 uppercase tracking-wider mb-1.5">Country</label>
                      <input type="text" value={country} onChange={(e) => setCountry(e.target.value)}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-4 focus:ring-primary/20 focus:border-primary focus:bg-white/15 transition-all"
                        placeholder="United States" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-white/70 uppercase tracking-wider mb-1.5">Tax ID (optional)</label>
                      <input type="text" value={taxId} onChange={(e) => setTaxId(e.target.value)}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-4 focus:ring-primary/20 focus:border-primary focus:bg-white/15 transition-all"
                        placeholder="For tax reporting" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-white/70 uppercase tracking-wider mb-2">Payment method</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                      {[
                        { key: 'paypal', label: 'PayPal', icon: '💳' },
                        { key: 'wise', label: 'Wise', icon: '🌍' },
                        { key: 'payoneer', label: 'Payoneer', icon: '🏧' },
                        { key: 'bank_transfer', label: 'Bank', icon: '🏦' },
                      ].map((m) => (
                        <button
                          key={m.key}
                          type="button"
                          onClick={() => setPaymentMethod(m.key)}
                          className={`flex flex-col items-center gap-1 py-3 rounded-xl text-xs font-bold transition-all ${
                            paymentMethod === m.key
                              ? 'bg-gradient-to-br from-primary to-orange-500 text-white shadow-lg scale-105'
                              : 'bg-white/10 text-white/70 hover:bg-white/15'
                          }`}
                        >
                          <span className="text-lg">{m.icon}</span>
                          <span>{m.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Conditional fields based on payment method */}
                  {paymentMethod === 'bank_transfer' ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[11px] font-bold text-white/70 uppercase tracking-wider mb-1">Bank Name</label>
                          <input type="text" value={bankName} onChange={(e) => setBankName(e.target.value)}
                            className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-4 focus:ring-primary/20 focus:border-primary text-sm"
                            placeholder="Chase Bank" />
                        </div>
                        <div>
                          <label className="block text-[11px] font-bold text-white/70 uppercase tracking-wider mb-1">Account Holder</label>
                          <input type="text" value={bankAccountHolder} onChange={(e) => setBankAccountHolder(e.target.value)}
                            className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-4 focus:ring-primary/20 focus:border-primary text-sm"
                            placeholder="Full name on account" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[11px] font-bold text-white/70 uppercase tracking-wider mb-1">Account Number / IBAN</label>
                          <input type="text" value={bankAccountNumber} onChange={(e) => setBankAccountNumber(e.target.value)}
                            className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-4 focus:ring-primary/20 focus:border-primary text-sm font-mono"
                            placeholder="1234567890 or IBAN" />
                        </div>
                        <div>
                          <label className="block text-[11px] font-bold text-white/70 uppercase tracking-wider mb-1">Routing / SWIFT / BIC</label>
                          <input type="text" value={bankRoutingSwift} onChange={(e) => setBankRoutingSwift(e.target.value)}
                            className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-4 focus:ring-primary/20 focus:border-primary text-sm font-mono"
                            placeholder="021000021 or BOFAUS3N" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-[11px] font-bold text-white/70 uppercase tracking-wider mb-1">
                        {paymentMethod === 'paypal' ? 'PayPal Email' : paymentMethod === 'wise' ? 'Wise Email' : paymentMethod === 'payoneer' ? 'Payoneer Email' : 'Payment Email'}
                      </label>
                      <input type="email" value={paymentEmail} onChange={(e) => setPaymentEmail(e.target.value)}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-4 focus:ring-primary/20 focus:border-primary focus:bg-white/15 transition-all"
                        placeholder="you@example.com" />
                    </div>
                  )}
                </div>

                <button type="submit" disabled={loading}
                  className="group relative w-full overflow-hidden bg-gradient-to-r from-primary to-orange-500 hover:shadow-xl hover:shadow-primary/30 disabled:opacity-60 disabled:cursor-not-allowed text-white font-extrabold py-4 rounded-xl transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2">
                  {loading ? (<>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting…
                  </>) : (<>
                    Submit Application
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </>)}
                  {!loading && <div className="absolute inset-0 shine-btn" />}
                </button>
                <p className="text-xs text-white/50 text-center pt-2">By applying you agree to our <Link to="/terms" className="underline hover:text-white">Terms</Link> and <Link to="/privacy-policy" className="underline hover:text-white">Privacy Policy</Link>.</p>
              </form>
            </Reveal>
          )}
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <Reveal>
            <div className="text-center mb-10">
              <div className="text-xs font-bold text-primary uppercase tracking-widest mb-2">Got questions?</div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight">Common Questions</h2>
            </div>
          </Reveal>
          <div className="space-y-3">
            {[
              { q: 'How much can I earn?', a: 'You earn 10% of each subscription from users who sign up with your code, paid monthly for as long as they stay subscribed.' },
              { q: 'When do I get paid?', a: 'Payouts are processed monthly for balances over $50. Smaller amounts roll over to the next month.' },
              { q: 'Do I need a minimum following?', a: 'We welcome creators of all sizes — what matters most is an engaged, relevant audience.' },
              { q: 'Is there a contract?', a: 'No long-term commitment. You can leave the program at any time.' },
            ].map((item, i) => (
              <Reveal key={i} delay={0.06 * i}>
                <details className="group bg-gray-50 border border-gray-100 rounded-2xl overflow-hidden hover:border-primary/30 transition-colors">
                  <summary className="flex items-center justify-between cursor-pointer px-6 py-4 list-none">
                    <h3 className="text-base font-bold text-gray-900 pr-4">{item.q}</h3>
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-primary font-bold group-open:rotate-45 transition-transform duration-300">+</span>
                  </summary>
                  <div className="px-6 pb-5 text-gray-600 leading-relaxed text-sm">{item.a}</div>
                </details>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <div className="text-center mt-8">
              <Link to="/contact" className="inline-flex items-center gap-2 text-primary font-semibold hover:underline group">
                Have other questions? Contact us
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default InfluencerLanding;
